import { Injectable, computed, inject } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import type { PermissionLevel } from './permission.types';
import {
  PERMISSION_LEVEL_EXECUTE,
  PERMISSION_LEVEL_NO_ACCESS,
  PERMISSION_LEVEL_READ_ONLY,
} from './permission.types';
import { permissionLevelMeets } from './permission-level.util';
import { resolveModuleKeyForPath } from './platform-permission.registry';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly auth = inject(AuthService);

  readonly isSuperAdmin = computed(
    () => this.auth.currentUser()?.roleKey === 'SUPER_ADMIN',
  );

  private effective() {
    return this.auth.currentUser()?.permissions ?? null;
  }

  canAccessModule(moduleKey: string): boolean {
    if (this.auth.currentUser()?.roleKey === 'SUPER_ADMIN') return true;
    return this.effective()?.modules?.[moduleKey]?.access === true;
  }

  getActionLevel(moduleKey: string, actionKey: string): PermissionLevel {
    if (this.auth.currentUser()?.roleKey === 'SUPER_ADMIN') {
      return PERMISSION_LEVEL_EXECUTE;
    }
    const mod = this.effective()?.modules?.[moduleKey];
    if (!mod?.access) return PERMISSION_LEVEL_NO_ACCESS;
    return mod.actions?.[actionKey] ?? PERMISSION_LEVEL_NO_ACCESS;
  }

  meets(moduleKey: string, actionKey: string, required: PermissionLevel): boolean {
    if (!this.canAccessModule(moduleKey)) return false;
    return permissionLevelMeets(this.getActionLevel(moduleKey, actionKey), required);
  }

  canRead(moduleKey: string, actionKey = 'read'): boolean {
    return this.meets(moduleKey, actionKey, PERMISSION_LEVEL_READ_ONLY);
  }

  canExecute(moduleKey: string, actionKey: string): boolean {
    return this.meets(moduleKey, actionKey, PERMISSION_LEVEL_EXECUTE);
  }

  /**
   * Show UI control when action is not No Access.
   * Pair with canExecute: show when canShow, enable click when canExecute.
   * Read Only → visible + disabled; No Access → hidden; Execute → visible + clickable.
   */
  canShow(moduleKey: string, actionKey: string): boolean {
    return this.getActionLevel(moduleKey, actionKey) !== PERMISSION_LEVEL_NO_ACCESS;
  }

  canAccessPath(pathname: string): boolean {
    const moduleKey = resolveModuleKeyForPath(pathname);
    if (!moduleKey) return true;
    return this.canAccessModule(moduleKey);
  }
}
